<template>
    <div class="form-area col-12 col-lg-auto">
        <!-- Modal to insert/edit a value -->
        <edit-modal
            v-model:is-open="isDosageModalOpen"
            :index="modalDosageIndex"
            :data="modalData"
            :is-steady-state-allowed="isSteadyStateAllowed"
            @save="handleSave"
        />

        <!-- Table listing all defined values -->
        <collapsible :title="$t('dosages.table.history')" :is-open="true">
            <table class="selectable">
                <thead>
                    <tr>
                        <th>{{ $t('dosages.table.from') }}</th>
                        <th>{{ $t('dosages.table.to') }}</th>
                        <th>{{ $t('dosages.table.dose') }}</th>
                        <th>{{ $t('dosages.table.interval') }}</th>
                        <th>{{ $t('dosages.table.infusion') }}</th>
                        <th class="text-right">
                            <button type="button" class="btn dark circle" @click="openDosageModal(null)">
                                <img src="@/assets/pictos/plus.svg" :alt="$t('action.add')" />
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(dosage, index) in requestData.dosages"
                        :key="dosage"
                        :class="{'selected': (index === selectedDosageIndex)}"
                        @click="selectedDosageIndex = index"
                    >
                        <dosage-row :dosage-time-range="dosage">
                            <template v-slot:tools>
                                <button type="button" class="btn link" @click="openDosageModal(index)">
                                    <img src="@/assets/pictos/pencil.svg" :alt="$t('action.edit')" />
                                </button>
                                <button type="button" class="btn dark circle" @click="requestData.removeNthDosage(index)">
                                    <img src="@/assets/pictos/minus.svg" :alt="$t('action.remove')" />
                                </button>
                            </template>
                        </dosage-row>
                    </tr>
                </tbody>
            </table>
        </collapsible>
    </div>

    <div class="data-area col-12 col-lg">
        <!-- Modal to edit chart settings -->
        <chart-settings-modal
            :graph-settings-title="$t('chartSettings.groupName.dosageSettings')"
            v-model:is-open="isSettingsModalOpen"
            v-model:graph-settings="chartStore.dosagesSettings"
        />

        <concentration-chart
            :graph-settings="chartStore.dosagesSettings"
            :selected-dosage-id="selectedDosageIndex"
            @open-settings="openSettingsModal()"
        />
    </div>
</template>

<script lang="ts">
import Collapsible from '@/components/Collapsible.vue';
import { DosageTimeRange } from '@/service/models/request/computation/dosage-time-range';
import { useRequestDataStore } from '@/store/request-data';
import { defineComponent, ref } from 'vue';
import EditModal from './EditModal.vue';
import DosageRow from './DosageRow.vue';
import ConcentrationChart from '@/components/ConcentrationChart.vue';
import { useChartsStore } from '@/store/charts';
import ChartSettingsModal from '@/components/ChartSettingsModal.vue';
import { DosageSteadyState } from '@/service/models/common/query-dosage/parts/dosage-steady-state';

export default defineComponent({
    components: {
        ChartSettingsModal,
        Collapsible,
        ConcentrationChart,
        DosageRow,
        EditModal,
    },

    setup()
    {
        const isDosageModalOpen = ref(false);
        const isSettingsModalOpen = ref(false);
        const modalDosageIndex = ref(0);
        const selectedDosageIndex = ref(0);
        const modalData = ref<DosageTimeRange|null>(null);
        const isSteadyStateAllowed = ref(true);

        const requestData = useRequestDataStore();
        const chartStore = useChartsStore();

        function openDosageModal(dosageIndex: number|null): void
        {
            if (dosageIndex === null) {
                modalDosageIndex.value = requestData.dosages.length;
                modalData.value = null;
            }
            else {
                modalDosageIndex.value = dosageIndex;
                modalData.value = requestData.dosages[dosageIndex];
            }

            // There can be only one dosage at Steady state
            isSteadyStateAllowed.value = true;
            for (let i = 0; i < requestData.dosages.length; i++) {
                if (i === dosageIndex) {
                    continue;
                }

                if (requestData.dosages[i].dosage.dosageRepeat instanceof DosageSteadyState) {
                    isSteadyStateAllowed.value = false;
                    break;
                }
            }

            isDosageModalOpen.value = true;
        }

        function openSettingsModal()
        {
            isSettingsModalOpen.value = true;
        }

        function handleSave(payload: {index: number, data: DosageTimeRange})
        {
            // Save data in the store
            requestData.dosages[payload.index] = payload.data;
        }

        return {
            requestData,
            chartStore,
            selectedDosageIndex,
            handleSave,
            openDosageModal, isDosageModalOpen, modalDosageIndex, modalData, isSteadyStateAllowed,
            openSettingsModal, isSettingsModalOpen,
        };
    }
})
</script>
